import Container from "@material-ui/core/Container";
import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  getRounds,
  getAllArbitrators,
  getAllTeams,
} from "../../../../reducers/adminSelectors";
import { saveNewPairing } from "../../../../actions/adminActions";
import _ from "lodash/fp";
import Grid from "@material-ui/core/Grid";
import { utcToZonedTime, format } from "date-fns-tz";
import Select from "../../../common/Select";
import TextInput from "../../../common/TextInput";
import { Alert, Checkbox } from "@mui/material";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Link, useHistory } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { ArbitratorConflictDropDown } from "../../../ArbitratorConflictDropDown";
import { getRoundDateTimeUTC } from "../../../../utils/dateUtils";
import { buildArbitratorOptionList } from "../../../../utils/arbitratorUtils";
import { getCompleteOralArgumentSchedule } from "../../../../reducers/adminSelectors";
import { ROUND_TYPES } from "../../../../utils/roundUtils";
import { isNotEmpty } from "../../../../utils/funcUtils";

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

const CreatePairing = ({
  getAllArbitrators = _.noop,
  getRounds = _.noop,
  allArbitrators = [],
  createPairing = _.noop,
  pairingSaveError,
  teams = [],
  oralArgumentSchedule,
  getAllTeams = _.noop,
  getOralArgumentSchedule = _.noop,
  rounds = [],
  saveNewPairing = _.noop,
}) => {
  const history = useHistory();

  const classes = useStyles();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saved, setSaved] = useState(false);

  const [options, setOptions] = useState([]);

  const [newPairing, setNewPairing] = useState({});
  const onSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const result = await saveNewPairing(newPairing);
    setIsSaving(false);
    if (result) {
      setSaved(true);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSaved(false);
  };

  const recalculateOptions = () => {
    const options = buildArbitratorOptionList(
      newPairing.claimant,
      newPairing.respondent,
      allArbitrators,
      newPairing.pairingTime,
      oralArgumentSchedule,
      newPairing.id,
    );
    setOptions(options);
  };

  useEffect(() => {
    recalculateOptions();
  }, [newPairing]);

  React.useEffect(() => {
    const asyncFetchData = async () => {
      try {
        setIsLoading(true);
        await getRounds();
        await getAllArbitrators();
        await getAllTeams();
        await getOralArgumentSchedule();
        recalculateOptions();
        setIsLoading(false);
      } catch (e) {
        //TODO ?
        setIsLoading(false);
      }
    };
    asyncFetchData();
  }, [oralArgumentSchedule]);

  const [roundDates, setRoundDates] = useState([]);
  const [roundTimes, setRoundTimes] = useState([]);

  useEffect(() => {
    if (isNotEmpty(rounds)) {
      const generalRound = _.head(
        _.filter((r) => r.roundType === ROUND_TYPES.GENERAL_ROUNDS, rounds),
      );
      const defaultPairingTime = getRoundDateTimeUTC(
        generalRound.availableDates[0],
        generalRound.availableTimes[0],
      );
      setNewPairing({
        claimantId: null,
        claimant: null,
        respondentId: null,
        respondent: null,
        claimantPractice: null,
        respondentPractice: null,
        pairingTime: defaultPairingTime,
        pairingDateString: format(
          utcToZonedTime(defaultPairingTime, "Asia/Hong_Kong"),
          "MM/dd/yyyy",
        ),
        pairingTimeString: format(
          utcToZonedTime(defaultPairingTime, "Asia/Hong_Kong"),
          "hh:mm aaa",
        ),
        hearingLink: null,
        room: null,
        chairArbitratorId: -1,
        secondArbitratorId: -1,
        thirdArbitratorId: -1,
        firstReserveArbitratorId: -1,
        secondReserveArbitratorId: -1,
        thirdReserveArbitratorId: -1,
      });

      setRoundDates(generalRound.availableDates);
      setRoundTimes(generalRound.availableTimes);
    }
  }, [rounds]);

  const availableSchools = _.map(
    (t) => {
      return { id: t.id, name: t.school };
    },
    _.filter((t) => t.status === "accepted", teams),
  );

  return (
    <Container maxWidth="xl" className={classes.root}>
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <>
          <form onSubmit={onSave}>
            <Button
              style={{ margin: "0 0 2rem 0" }}
              variant="text"
              component={Link}
              onClick={() => history.go(-1)}
            >
              <ArrowBackIosIcon /> Back to Oral Arguments
            </Button>
            <Typography variant="h4">New Pairing</Typography>
            <Grid
              container
              style={{ flexGrow: 1, margin: "1rem 0" }}
              spacing={2}
            >
              <Grid item xs={12} md={6}>
                <Select
                  name="claimant"
                  onChange={(e) =>
                    setNewPairing({
                      ...newPairing,
                      claimant: _.find((t) => t.id === e.target.value, teams),
                      claimantId: e.target.value,
                    })
                  }
                  value={newPairing.claimantId}
                  label="Claimant"
                  options={availableSchools}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newPairing.claimantPractice}
                        name="claimantPractice"
                        onChange={() =>
                          setNewPairing({
                            ...newPairing,
                            claimantPractice: !newPairing.claimantPractice,
                          })
                        }
                        color="primary"
                      />
                    }
                    label="Claimant Practice?"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  name="respondent"
                  onChange={(e) =>
                    setNewPairing({
                      ...newPairing,
                      respondent: _.find((t) => t.id === e.target.value, teams),
                      respondentId: e.target.value,
                    })
                  }
                  value={newPairing.respondentId}
                  label="Respondent"
                  options={availableSchools}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newPairing.respondentPractice}
                        onChange={() =>
                          setNewPairing({
                            ...newPairing,
                            respondentPractice: !newPairing.respondentPractice,
                          })
                        }
                        name="respondentPractice"
                        color="primary"
                      />
                    }
                    label="Respondent Practice?"
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Select
                      name="pairingDates"
                      required
                      onChange={(e) =>
                        setNewPairing({
                          ...newPairing,
                          pairingDateString: e.target.value,
                          pairingTime: getRoundDateTimeUTC(
                            e.target.value,
                            newPairing.pairingTimeString,
                          ),
                        })
                      }
                      value={newPairing.pairingDateString}
                      label="Date"
                      options={roundDates}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Select
                      name="pairingTimes"
                      required
                      onChange={(e) =>
                        setNewPairing({
                          ...newPairing,
                          pairingTimeString: e.target.value,
                          pairingTime: getRoundDateTimeUTC(
                            newPairing.pairingDateString,
                            e.target.value,
                          ),
                        })
                      }
                      value={newPairing.pairingTimeString}
                      label="Time"
                      options={roundTimes}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextInput
                    name="hearingLink"
                    notRequired
                    onChange={(e) =>
                      setNewPairing({
                        ...newPairing,
                        hearingLink: e.target.value,
                      })
                    }
                    value={newPairing.hearingLink}
                    label="Virtual Link"
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextInput
                    name="room"
                    notRequired
                    onChange={(e) =>
                      setNewPairing({ ...newPairing, room: e.target.value })
                    }
                    value={newPairing.room}
                    label="Room"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArbitratorConflictDropDown
                    name="chairArbitrator"
                    onChange={(e) =>
                      setNewPairing({
                        ...newPairing,
                        chairArbitratorId: e.target.value,
                      })
                    }
                    value={newPairing.chairArbitratorId}
                    label="Arbitrator 1"
                    options={options}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArbitratorConflictDropDown
                    name="secondArbitrator"
                    onChange={(e) =>
                      setNewPairing({
                        ...newPairing,
                        secondArbitratorId: e.target.value,
                      })
                    }
                    value={newPairing.secondArbitratorId}
                    label="Arbitrator 2"
                    options={options}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArbitratorConflictDropDown
                    name="thirdArbitrator"
                    onChange={(e) =>
                      setNewPairing({
                        ...newPairing,
                        thirdArbitratorId: e.target.value,
                      })
                    }
                    value={newPairing.thirdArbitratorId}
                    label="Arbitrator 3"
                    options={options}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArbitratorConflictDropDown
                    name="firstReserveArbitrator"
                    onChange={(e) =>
                      setNewPairing({
                        ...newPairing,
                        firstReserveArbitratorId: e.target.value,
                      })
                    }
                    value={newPairing.firstReserveArbitratorId}
                    label="First Reserve"
                    options={options}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArbitratorConflictDropDown
                    name="secondReserveArbitrator"
                    onChange={(e) =>
                      setNewPairing({
                        ...newPairing,
                        secondReserveArbitratorId: e.target.value,
                      })
                    }
                    value={newPairing.secondReserveArbitratorId}
                    label="Second Reserve"
                    options={options}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArbitratorConflictDropDown
                    name="thirdReserveArbitrator"
                    onChange={(e) =>
                      setNewPairing({
                        ...newPairing,
                        thirdReserveArbitratorId: e.target.value,
                      })
                    }
                    value={newPairing.thirdReserveArbitratorId}
                    label="Third Reserve"
                    options={options}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={12}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    disabled={isSaving}
                  >
                    Create Pairing
                  </Button>
                  {isSaving && (
                    <CircularProgress
                      size={30}
                      style={{ marginLeft: "2rem" }}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
            {pairingSaveError && (
              <Typography color="error">{pairingSaveError}</Typography>
            )}
            <Snackbar
              open={saved}
              onClose={handleClose}
              autoHideDuration={6000}
            >
              <Alert
                onClose={handleClose}
                severity="success"
                sx={{ width: "100%" }}
              >
                Pairing saved
              </Alert>
            </Snackbar>
          </form>
        </>
      )}
    </Container>
  );
};

CreatePairing.propTypes = {
  allArbitrators: PropTypes.array,
  getRounds: PropTypes.func,
};

export default connect(
  (state, ownProps) => ({
    teams: state.admin.teams,
    allArbitrators: state.admin.arbitrators,
    pairingSaveError: state.admin.pairingSaveError,
    oralArgumentSchedule: state.admin.completeOralArgumentSchedule,
    rounds: state.admin.rounds,
  }),
  {
    getAllArbitrators: getAllArbitrators,
    getAllTeams: getAllTeams,
    saveNewPairing: saveNewPairing,
    getRounds: getRounds,
    getOralArgumentSchedule: getCompleteOralArgumentSchedule,
  },
)(CreatePairing);
